import { OrganizationInvitationErrorTypes } from "@/api";
import { TypographyH3 } from "@/components/typography/H3";
import { TypographyP } from "@/components/typography/P";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import Feedback from "@/components/ui/feedback";
import { LogoutButton } from "@/features/user-settings";
import { ApolloError } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { MessageCircleQuestion } from "lucide-react";

type Props = {
  error: ApolloError | OrganizationInvitationErrorTypes;
};

export const InvitationError = ({ error }: Props) => {
  const { user } = useAuth0();
  if (error instanceof ApolloError && error.message === "access denied") {
    return (
      <div>
        <TypographyH3 className="mb-8">
          Whoops! There was an issue accessing the invitation
        </TypographyH3>
        <TypographyP>
          It looks like there was a problem accessing your invitation. This
          might be because the email address you&apos;re logged in with does not
          match the email address the invitation was sent to.
          <br />
          <br />
          To resolve this you can:
          <ol className="list-outside list-disc pl-5">
            <li className="">
              Log out and log in with the correct email address that received
              the invitation
            </li>
            {user?.email && (
              <li className="">
                Ask for a new invitation for{" "}
                <span className="font-semibold">{user.email}</span>
              </li>
            )}
          </ol>
          <br />
          <br />
          If you need further help, feel free to reach out. Thank you!
        </TypographyP>
        <div className="flex gap-4 justify-center mt-12">
          <LogoutButton />
          <Feedback>
            {() => (
              <Dialog.Trigger asChild>
                <Button variant="ghost">
                  <MessageCircleQuestion
                    strokeWidth={1.6}
                    className="h-[20px] w-[20px] relative -left-2 -top-0.5 inline"
                  />
                  Report Issue
                </Button>
              </Dialog.Trigger>
            )}
          </Feedback>
        </div>
      </div>
    );
  }

  if (error && "__typename" in error) {
    if (error.__typename === "OrganizationInvitationNotFound") {
      return (
        <Alert
          title="Invitation could not be found"
          description="The invitation could not be found. Ask your administrator to send you a new invitation"
        />
      );
    }

    if (error.__typename === "OrganizationNotFound") {
      return (
        <Alert
          title="Organization could not be found"
          description="The organization could not be found. Ask your administrator to send you a new invitation"
        />
      );
    }
  }

  return (
    <Alert
      title="An error happened"
      description="An unknown error happened, please try again."
    />
  );
};
